import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, ChevronDown, Library, Volume2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Unique gradient colors for avatars
const AVATAR_COLORS_FEMALE = [
  'from-rose-400 to-pink-600',
  'from-purple-400 to-fuchsia-600',
  'from-violet-400 to-purple-600',
  'from-pink-400 to-rose-600',
  'from-fuchsia-400 to-pink-600',
  'from-red-400 to-rose-600',
  'from-amber-400 to-orange-500',
  'from-teal-400 to-emerald-600',
];
const AVATAR_COLORS_MALE = [
  'from-blue-400 to-indigo-600',
  'from-cyan-400 to-blue-600',
  'from-indigo-400 to-violet-600',
  'from-sky-400 to-blue-600',
  'from-slate-400 to-gray-600',
  'from-emerald-400 to-green-600',
  'from-teal-400 to-cyan-600',
  'from-blue-500 to-purple-600',
];

const getAvatarColor = (name, gender) => {
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const colors = gender === 'female' ? AVATAR_COLORS_FEMALE : AVATAR_COLORS_MALE;
  return colors[hash % colors.length];
};

const VoiceCard = ({ voice }) => {
  const color = getAvatarColor(voice.base_voice || voice.name, voice.gender);
  const isFemale = voice.gender === 'female';

  return (
    <div data-testid={`voice-card-${voice.id}`}
      className="glass-card card-hover rounded-xl p-4 border border-border transition-all group">
      <div className="flex items-center gap-3">
        {/* Unique avatar with gender distinction */}
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform`}>
          {isFemale ? (
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white/90" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="8" r="4" /><path d="M12 12c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" />
              <path d="M8 6.5c0-2 1-4 4-4s4 2 4 4" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white/90" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="8" r="4" /><path d="M12 12c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" />
              <path d="M7 7c0-3 2-5 5-5s5 2 5 5" strokeLinecap="round" />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-foreground truncate">{voice.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {voice.gender === 'female' ? 'Female' : 'Male'} · {voice.language}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          voice.category === 'Conversational' ? 'bg-green-500/10 text-green-500' :
          voice.category === 'Professional' ? 'bg-blue-500/10 text-blue-500' :
          voice.category === 'Narration' ? 'bg-purple-500/10 text-purple-500' :
          'bg-amber-500/10 text-amber-500'
        }`}>
          {voice.category}
        </span>
      </div>
      {voice.description && (
        <p className="mt-2 text-xs text-muted-foreground truncate">{voice.description}</p>
      )}
    </div>
  );
};

const VoiceLibrary = () => {
  const { token, API } = useAuth();
  const [voices, setVoices] = useState([]);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/voices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVoices(r.data.voices))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, API]);

  const languages = useMemo(() => [...new Set(voices.map(v => v.language))].sort(), [voices]);
  const categories = useMemo(() => [...new Set(voices.map(v => v.category))].sort(), [voices]);

  const filtered = useMemo(() => {
    return voices.filter(v => {
      if (langFilter && v.language !== langFilter) return false;
      if (genderFilter && v.gender !== genderFilter) return false;
      if (catFilter && v.category !== catFilter) return false;
      if (search && !v.name.toLowerCase().includes(search.toLowerCase()) && !v.language.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [voices, langFilter, genderFilter, catFilter, search]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Library className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-bold font-outfit text-foreground text-lg">Voice Library</h2>
          <p className="text-xs text-muted-foreground">{voices.length} voices · {languages.length} languages</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Search voices..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm focus:border-primary" />
        </div>
        <div className="relative">
          <select value={langFilter} onChange={e => setLangFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm cursor-pointer">
            <option value="">All Languages</option>
            {languages.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
        </div>
        <div className="relative">
          <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm cursor-pointer">
            <option value="">All Genders</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
        </div>
        <div className="relative">
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm cursor-pointer">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{filtered.length} voice{filtered.length !== 1 ? 's' : ''} found</p>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.slice(0, 60).map(v => <VoiceCard key={v.id} voice={v} />)}
      </div>
      {filtered.length > 60 && (
        <p className="text-center text-xs text-muted-foreground py-4">Showing first 60 of {filtered.length} voices. Use search and filters to find more.</p>
      )}
    </div>
  );
};

export default VoiceLibrary;
